import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { itemsAPI, itemCommentsAPI, categoriesAPI, categoryGroupsAPI, vansAPI, warehouseAPI, partCategoriesAPI } from '../lib/api';
import { predictiveRestockAPI } from '../lib/v2Api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { TruncatedTextWithTooltip } from '../components/TruncatedTextWithTooltip';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { IntegerQuantityInput } from '../components/IntegerQuantityInput';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { SearchableSelect } from '../components/ui/searchable-select';
import { MultiSelectCombobox } from '../components/ui/multi-select-combobox';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { DuplicatePartWarning, useDuplicateCheck } from '../components/DuplicatePartWarning';
import { ManualMergeModal } from '../components/ManualMergeModal';
import { Plus, Package, Loader2, Edit2, X, Save, MessageSquare, Send, User, Clock, Upload, Download, Tag, Filter, FolderOpen, FolderMinus, Trash2, AlertTriangle, ExternalLink, QrCode, Printer, Search, Archive, ArchiveRestore, Layers, ChevronDown, LayoutGrid, List, Image, Target, Warehouse as WarehouseIcon, Truck, Camera, ChevronRight, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ItemCodesManager } from '../components/ItemCodesManager';
import { LabelPrinter } from '../components/LabelPrinter';
import { BulkLabelPrinter } from '../components/BulkLabelPrinter';
import { ListPagination } from '../components/ListPagination';
import { UOM_OPTIONS, DEFAULT_UOM, formatQuantityWithUom } from '../lib/uom';
import { MobileFABContainer, MobileFABMenu, MobileFABMenuItem } from '../components/MobileFAB';
import { KebabMenu } from '../components/KebabMenu';
import { ImageThumbnail } from '../components/ImageThumbnail';
import { CategoryManagerDrawer } from '../components/CategoryManagerDrawer';

export const Catalogue = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoadDone = useRef(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', sku: '', manufacturer_part_number: '', unit_cost: '', billable_price: '', reorder_threshold: '', category_ids: [], external_url: '', uom: DEFAULT_UOM });
  const [createImageFile, setCreateImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  // Phase 1: duplicate detection — runs before create/edit save.
  const dupCheck = useDuplicateCheck({ context: 'catalogue' });
  
  // Codes manager state
  const [codesManagerOpen, setCodesManagerOpen] = useState(false);
  const [labelPrinterOpen, setLabelPrinterOpen] = useState(false);
  const [bulkLabelPrinterOpen, setBulkLabelPrinterOpen] = useState(false);
  const [selectedItemForCodes, setSelectedItemForCodes] = useState(null);
  
  // Phase 3.5: URL-synced filter and pagination state
  const categoryFilter = searchParams.get('category') || 'all';
  const groupFilter = searchParams.get('group') || 'all';
  const searchFilter = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('per_page')) || 25;
  const showArchived = searchParams.get('archived') === 'true';
  
  // Search is fully decoupled from React Router to avoid per-keystroke navigation.
  // searchInput = controlled input value (instant)
  // debouncedSearch = value used by fetchItems (delayed 300ms)
  // URL is synced silently via history.replaceState (no RR navigation)
  const [searchInput, setSearchInput] = useState(searchFilter);
  const [debouncedSearch, setDebouncedSearch] = useState(searchFilter);
  const searchDebounceRef = useRef(null);
  // Ref for search input to maintain focus
  const searchInputRef = useRef(null);
  
  // Debounce: searchInput → debouncedSearch + silent URL sync
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (searchInput === debouncedSearch) return;
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      // Silently update URL bar without triggering React Router navigation
      const url = new URL(window.location.href);
      if (searchInput) {
        url.searchParams.set('search', searchInput);
      } else {
        url.searchParams.delete('search');
      }
      url.searchParams.delete('page');
      window.history.replaceState(null, '', url.pathname + (url.search || ''));
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchInput, debouncedSearch]);
  
  // Handle browser back/forward: sync URL → local search state
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const urlSearch = params.get('search') || '';
      setSearchInput(urlSearch);
      setDebouncedSearch(urlSearch);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  
  // Server response pagination data
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Helper to update URL params
  const updateParams = useCallback((updates) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '' || value === 'all' || value === 'false' || (key === 'page' && value === 1) || (key === 'per_page' && value === 25)) {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);
  
  // URL-synced setters
  const setCategoryFilter = useCallback((value) => {
    // Toggle behavior: if clicking the same category, deselect it (go to 'all')
    const newValue = value === categoryFilter ? 'all' : value;
    updateParams({ category: newValue, page: 1 });
  }, [updateParams, categoryFilter]);
  
  // Direct clear category filter (for clear buttons)
  const clearCategoryFilter = useCallback(() => {
    updateParams({ category: 'all', page: 1 });
  }, [updateParams]);
  
  const setGroupFilter = useCallback((value) => {
    updateParams({ group: value });
  }, [updateParams]);
  
  const setPage = useCallback((value) => {
    updateParams({ page: value });
  }, [updateParams]);
  
  const setPageSize = useCallback((value) => {
    updateParams({ per_page: value, page: 1 });
  }, [updateParams]);
  
  const setShowArchived = useCallback((value) => {
    updateParams({ archived: value ? 'true' : null, page: 1 });
  }, [updateParams]);
  
  // Archive/Restore dialog state
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);

  // Archived-with-stock alert (admin warning at top of catalogue)
  const [archivedWithStock, setArchivedWithStock] = useState({ items: [], total_items: 0, total_qty_on_hand: 0 });
  const [archivedAlertOpen, setArchivedAlertOpen] = useState(false);
  
  // Hard delete dialog state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteBlockers, setDeleteBlockers] = useState(null);
  
  // Mobile UI state
  const [fabOpen, setFabOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  
  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  
  // Stock targets state (for edit mode)
  const [editStockTargets, setEditStockTargets] = useState([]);
  const [targetLocations, setTargetLocations] = useState({ warehouses: [], vans: [] });

  // Manual merge modal state — opened from the inline "Edit" surface as the
  // narrow first-version entry point for owner-driven manual item merges.
  const [manualMergeItem, setManualMergeItem] = useState(null);
  const [manualMergeOpen, setManualMergeOpen] = useState(false);

  // Comments state
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Category management state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryGroupId, setNewCategoryGroupId] = useState('');
  const [newCategoryThumbnailUrl, setNewCategoryThumbnailUrl] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryGroupId, setEditCategoryGroupId] = useState('');
  const [editCategoryThumbnailUrl, setEditCategoryThumbnailUrl] = useState('');

  // Category Groups management state
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [savingGroup, setSavingGroup] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState('');

  // Category bulk selection state (for bulk delete)
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(new Set());
  const [deletingCategories, setDeletingCategories] = useState(false);

  // Clear category state (for tile quick action)
  const [clearCategoryDialogOpen, setClearCategoryDialogOpen] = useState(false);
  const [categoryToClear, setCategoryToClear] = useState(null);
  const [clearingCategory, setClearingCategory] = useState(false);

  // Bulk selection state (owner only) - checkboxes always visible
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(null);
  const [bulkCategoryId, setBulkCategoryId] = useState('uncategorized');
  const [bulkReorderThreshold, setBulkReorderThreshold] = useState('');
  const [bulkActionSaving, setBulkActionSaving] = useState(false);
  
  // Global selection: "Select All matching" mode
  // When true, ALL matching items are selected except those in excludedIds
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [matchingCount, setMatchingCount] = useState(0);
  const [frozenQuery, setFrozenQuery] = useState(null);
  const [excludedIds, setExcludedIds] = useState(new Set());
  
  // NEW: Bulk job progress tracking
  const [activeJobId, setActiveJobId] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  const [jobProgressDialogOpen, setJobProgressDialogOpen] = useState(false);
  
  // NEW: Hide inactive parts toggle (localStorage-backed, default ON)
  const getInitialHideInactive = () => {
    const stored = localStorage.getItem('catalogue_hideInactive');
    return stored === null ? true : stored === 'true';
  };
  const [hideInactive, setHideInactiveState] = useState(getInitialHideInactive);
  
  const setHideInactive = useCallback((value) => {
    setHideInactiveState(value);
    localStorage.setItem('catalogue_hideInactive', String(value));
    // Reset to page 1 when toggling
    updateParams({ page: 1 });
  }, [updateParams]);
  
  // Category tiles view state (localStorage-backed)
  const getInitialViewMode = () => {
    const stored = localStorage.getItem('catalogue_view_mode');
    return stored === 'tiles' ? 'tiles' : 'list';
  };
  const [viewMode, setViewModeState] = useState(getInitialViewMode);
  
  const setViewMode = useCallback((mode) => {
    setViewModeState(mode);
    localStorage.setItem('catalogue_view_mode', mode);
  }, []);
  
  // Categories with counts for tile view
  const [categoriesWithCounts, setCategoriesWithCounts] = useState([]);
  const [loadingCategoryCounts, setLoadingCategoryCounts] = useState(false);

  // Nested category browsing state
  const [browseParentId, setBrowseParentId] = useState(null);
  const [browseBreadcrumbs, setBrowseBreadcrumbs] = useState([]);
  const [browseCategories, setBrowseCategories] = useState([]);
  const [browseDirectItemCount, setBrowseDirectItemCount] = useState(0);
  const [browseTotalItems, setBrowseTotalItems] = useState(0);
  const [loadingBrowse, setLoadingBrowse] = useState(false);

  const fetchBrowseCategories = useCallback(async (parentId = null) => {
    setLoadingBrowse(true);
    try {
      const res = await partCategoriesAPI.browse(parentId);
      setBrowseParentId(parentId);
      setBrowseBreadcrumbs(res.data.breadcrumbs || []);
      setBrowseCategories(res.data.categories || []);
      setBrowseDirectItemCount(res.data.direct_item_count || 0);
      setBrowseTotalItems(res.data.total_items || 0);
    } catch (err) {
      console.error('Failed to browse categories', err);
    } finally {
      setLoadingBrowse(false);
    }
  }, []);

  const handleDrillDown = useCallback((categoryId) => {
    fetchBrowseCategories(categoryId);
  }, [fetchBrowseCategories]);

  const handleBreadcrumbNav = useCallback((targetId) => {
    fetchBrowseCategories(targetId || null);
  }, [fetchBrowseCategories]);

  const fetchCategoryGroups = async () => {
    try {
      const response = await categoryGroupsAPI.list();
      setCategoryGroups(response.data);
    } catch (error) {
      console.error('Failed to load category groups', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.list();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };
  
  const fetchCategoriesWithCounts = async () => {
    setLoadingCategoryCounts(true);
    try {
      const response = await categoriesAPI.listWithCounts();
      setCategoriesWithCounts(response.data);
    } catch (error) {
      console.error('Failed to load category counts', error);
    } finally {
      setLoadingCategoryCounts(false);
    }
  };

  // Phase 3.5: Server-side pagination and search
  const fetchItems = async (options = {}) => {
    // DEFENSIVE: If options is not an object (e.g., someone passed a string), convert it
    if (typeof options === 'string') {
      console.warn('[CATALOGUE_FETCH] fetchItems called with string instead of object, converting...');
      options = { categoryFilter: options };
    }
    
    const { silentRetry = false, retryCount = 0 } = options;
    
    // Don't set loading if this is a silent retry OR after initial load (prevents flash on search)
    if (!silentRetry && !initialLoadDone.current) {
      setLoading(true);
    }
    
    const fetchStartTime = Date.now();
    console.log('[CATALOGUE_FETCH] Starting items fetch...', { 
      silentRetry, 
      retryCount,
      showArchived,
      page: options.page ?? page,
      pageSize: options.pageSize ?? pageSize,
      categoryFilter: options.categoryFilter ?? categoryFilter,
      searchFilter: options.search ?? debouncedSearch
    });
    
    try {
      const params = { 
        archived: showArchived, 
        page: options.page ?? page,
        page_size: options.pageSize ?? pageSize,
      };
      
      // Add category filter
      const catFilter = options.categoryFilter ?? categoryFilter;
      if (catFilter && catFilter !== 'all') {
        params.category_id = catFilter;
      }
      
      // Add search filter (server-side search) - TYPE-SAFE
      const search = options.search ?? debouncedSearch;
      if (search && typeof search === 'string' && search.trim()) {
        params.search = search.trim();
      }
      
      // When hideInactive is ON and not viewing archived, filter to active only
      if (!showArchived && hideInactive) {
        params.active_only = true;
      }
      
      console.log('[CATALOGUE_FETCH] Calling itemsAPI.list with params:', params);
      const response = await itemsAPI.list(params);
      const data = response.data;
      
      console.log(`[CATALOGUE_FETCH] Success in ${Date.now() - fetchStartTime}ms:`, {
        itemCount: data.items?.length || 0,
        total: data.total,
        totalPages: data.total_pages
      });
      
      // Handle paginated response
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
      
      // Update page if response indicates different page
      if (data.page) {
        setPage(data.page);
      }
    } catch (error) {
      console.error(`[CATALOGUE_FETCH] FAILED in ${Date.now() - fetchStartTime}ms:`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        code: error.code,
        message: error.message,
        silentRetry,
        retryCount
      });
      
      // If this is a silent retry (post-operation refresh), try again
      if (silentRetry && retryCount < 2) {
        console.warn(`[CATALOGUE_FETCH] Silent retry ${retryCount + 1}/2...`);
        setTimeout(() => {
          fetchItems({ ...options, silentRetry: true, retryCount: retryCount + 1 });
        }, 1000 * (retryCount + 1)); // Exponential backoff
        return;
      }
      
      // Provide specific error messages based on error type
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 403) {
        toast.error(detail || 'Permission denied: You do not have access to view inventory');
      } else if (status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (status === 404) {
        toast.error('Inventory data not found');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. Please try again.');
      } else if (!navigator.onLine) {
        toast.error('No internet connection. Please check your network.');
      } else {
        toast.error(detail || 'Failed to load inventory. Please refresh the page.');
      }
      console.error('Inventory load error:', error);
    } finally {
      initialLoadDone.current = true;
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchCategoryGroups();
    fetchCategories();
    fetchItems();
    // Fetch nested category browsing for tile view
    fetchBrowseCategories(null);
  }, []);

  // Refetch when filters or pagination changes
  useEffect(() => {
    if (!initialLoadDone.current) return; // skip — initial load handles first fetch
    fetchItems({ page, pageSize, categoryFilter, search: debouncedSearch });
  }, [page, pageSize, categoryFilter, showArchived, debouncedSearch, hideInactive]);
  
  // Refetch browse categories when hide inactive changes (affects counts)
  useEffect(() => {
    if (viewMode === 'tiles') {
      fetchBrowseCategories(browseParentId);
    }
  }, [hideInactive, viewMode]);
  
  // Fetch matching count when filters change (for "Select all X matching" banner)
  useEffect(() => {
    const fetchMatchingCount = async () => {
      if (showArchived) {
        setMatchingCount(0);
        return;
      }
      try {
        const catId = categoryFilter !== 'all' ? categoryFilter : null;
        const searchTerm = debouncedSearch || null;
        const response = await itemsAPI.bulkCount(catId, searchTerm);
        setMatchingCount(response.data.matching_count);
      } catch (error) {
        console.error('Failed to fetch matching count', error);
      }
    };
    fetchMatchingCount();
  }, [categoryFilter, debouncedSearch, showArchived, hideInactive]);
  
  // Fetch the "archived items still holding stock" warning once on mount
  // and refresh after any archive/restore action.
  const fetchArchivedWithStock = useCallback(async () => {
    try {
      const r = await itemsAPI.archivedWithStock();
      setArchivedWithStock(r.data || { items: [], total_items: 0, total_qty_on_hand: 0 });
    } catch (e) {
      // non-fatal — just hide the alert
      setArchivedWithStock({ items: [], total_items: 0, total_qty_on_hand: 0 });
    }
  }, []);

  useEffect(() => {
    fetchArchivedWithStock();
  }, [fetchArchivedWithStock]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
    // Clear selection on page change
    setSelectedIds(new Set());
  };
  
  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setSelectedIds(new Set());
  };

  // Filter categories by selected group
  const filteredCategories = groupFilter === 'all' 
    ? categories 
    : groupFilter === 'ungrouped'
      ? categories.filter(c => !c.group_id)
      : categories.filter(c => c.group_id === groupFilter);

  // Phase 3.5: Search is now server-side, no client-side filtering needed
  // Just use items directly - the backend handles search, pagination, and filtering
  const filteredItems = items;

  // Count active filters (excluding search)
  const activeFilterCount = [
    categoryFilter !== 'all'
  ].filter(Boolean).length;

  // Clear filters except search
  const clearFiltersExceptSearch = () => {
    updateParams({ category: null, group: null, page: 1 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await dupCheck.guardedCreate(
        {
          name: formData.name,
          sku: formData.sku || null,
          manufacturer_part_number: formData.manufacturer_part_number || null,
          category_ids: formData.category_ids.length > 0 ? formData.category_ids : null,
        },
        () => _actuallyCreate(),
        async (existing) => {
          // "Use Existing Part": close create dialog and navigate to that
          // item's row so the user can edit it. Catalogue items.length is
          // large — easiest is to surface via the existing search/filter:
          // populate search with SKU/name and the row will appear.
          setDialogOpen(false);
          setFormData({ name: '', sku: '', manufacturer_part_number: '', unit_cost: '', billable_price: '', reorder_threshold: '', category_ids: [], external_url: '', uom: DEFAULT_UOM });
          setCreateImageFile(null);
          updateParams({ search: existing.sku || existing.name, page: 1 });
          toast.success(`Using existing part: ${existing.name}`);
          // Pull the freshly-filtered row and open its edit panel.
          await fetchItems({ categoryFilter });
          setTimeout(() => startEdit({
            id: existing.item_id, name: existing.name, sku: existing.sku,
            manufacturer_part_number: existing.manufacturer_part_number,
            category_ids: [], is_active: existing.is_active,
          }), 250);
        },
      );
    } finally {
      setSaving(false);
    }
  };
  const _actuallyCreate = async () => {
    try {
      const res = await itemsAPI.create({
        name: formData.name,
        sku: formData.sku || null,
        manufacturer_part_number: formData.manufacturer_part_number || null,
        unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null,
        billable_price: formData.billable_price ? parseFloat(formData.billable_price) : null,
        reorder_threshold: formData.reorder_threshold ? parseFloat(formData.reorder_threshold) : null,
        category_ids: formData.category_ids.length > 0 ? formData.category_ids : null,
        external_url: formData.external_url || null,
        uom: formData.uom || DEFAULT_UOM,
      });
      if (createImageFile && res.data?.id) {
        try { await itemsAPI.uploadImage(res.data.id, createImageFile); } catch { toast.error('Item saved but image upload failed'); }
      }
      toast.success('Item added successfully');
      setDialogOpen(false);
      setFormData({ name: '', sku: '', manufacturer_part_number: '', unit_cost: '', billable_price: '', reorder_threshold: '', category_ids: [], external_url: '', uom: DEFAULT_UOM });
      setCreateImageFile(null);
      fetchItems({ categoryFilter });
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const startEdit = async (item) => {
    setEditingId(item.id);
    setEditData({
      name: item.name,
      sku: item.sku || '',
      manufacturer_part_number: item.manufacturer_part_number || '',
      unit_cost: item.unit_cost?.toString() || '',
      billable_price: item.billable_price?.toString() || '',
      reorder_threshold: item.reorder_threshold?.toString() || '',
      category_ids: item.category_ids || [],
      external_url: item.external_url || '',
      is_active: item.is_active,
      uom: item.uom || DEFAULT_UOM,
      image_thumb_url: item.image_thumb_url || null,
    });
    // Fetch stock targets and locations in parallel
    try {
      const [targetsRes, whRes, vansRes] = await Promise.all([
        predictiveRestockAPI.listTargets({ item_id: item.id }),
        warehouseAPI.listWarehouses(),
        vansAPI.list(),
      ]);
      const existing = targetsRes.data || [];
      const warehouses = whRes.data || [];
      const vans = vansRes.data || [];
      setTargetLocations({ warehouses, vans });
      // Build target rows: one per warehouse + one per van, pre-fill existing values
      const rows = [];
      warehouses.forEach(wh => {
        const match = existing.find(t => t.source_type === 'warehouse' && t.source_id === wh.id);
        rows.push({ id: match?.id, item_id: item.id, source_type: 'warehouse', source_id: wh.id, source_name: wh.name, target_qty: match?.target_qty?.toString() || '' });
      });
      vans.forEach(v => {
        const match = existing.find(t => t.source_type === 'vehicle' && t.source_id === v.id);
        rows.push({ id: match?.id, item_id: item.id, source_type: 'vehicle', source_id: v.id, source_name: v.name, target_qty: match?.target_qty?.toString() || '' });
      });
      setEditStockTargets(rows);
    } catch {
      setEditStockTargets([]);
      setTargetLocations({ warehouses: [], vans: [] });
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
    setEditStockTargets([]);
  };

  const handleImageSelect = (file, setterFn) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Only JPG, PNG, or WebP images allowed'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2 MB'); return; }
    setterFn(file);
  };

  const handleEditImageUpload = async (itemId, file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Only JPG, PNG, or WebP allowed'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2 MB'); return; }
    try {
      const res = await itemsAPI.uploadImage(itemId, file);
      setEditData(prev => ({ ...prev, image_thumb_url: res.data.image_thumb_url }));
      toast.success('Image uploaded');
      fetchItems({ categoryFilter });
    } catch { toast.error('Image upload failed'); }
  };

  const handleEditImageDelete = async (itemId) => {
    try {
      await itemsAPI.deleteImage(itemId);
      setEditData(prev => ({ ...prev, image_thumb_url: null }));
      toast.success('Image removed');
      fetchItems({ categoryFilter });
    } catch { toast.error('Failed to remove image'); }
  };

  const saveEdit = async (itemId) => {
    setEditSaving(true);
    try {
      await dupCheck.guardedCreate(
        {
          name: editData.name,
          sku: editData.sku || null,
          manufacturer_part_number: editData.manufacturer_part_number || null,
          category_ids: editData.category_ids || [],
          exclude_item_id: itemId,
        },
        () => _actuallyUpdate(itemId),
        async (existing) => {
          // "Use Existing Part" on edit: stop the rename, surface the
          // existing record so the user can manually decide whether to
          // merge later (Phase 2). For now, just navigate to the duplicate.
          setEditingId(null);
          updateParams({ search: existing.sku || existing.name, page: 1 });
          toast.success(`Edit cancelled — surfacing existing part: ${existing.name}`);
          await fetchItems({ categoryFilter });
        },
      );
    } finally {
      setEditSaving(false);
    }
  };
  const _actuallyUpdate = async (itemId) => {
    try {
      await itemsAPI.update(itemId, {
        name: editData.name,
        sku: editData.sku || null,
        manufacturer_part_number: editData.manufacturer_part_number || null,
        unit_cost: editData.unit_cost ? parseFloat(editData.unit_cost) : null,
        billable_price: editData.billable_price ? parseFloat(editData.billable_price) : null,
        reorder_threshold: editData.reorder_threshold ? parseInt(editData.reorder_threshold) : null,
        category_ids: editData.category_ids || [],
        external_url: editData.external_url,
        is_active: editData.is_active,
      });
      // Save stock targets (only those with a value set)
      const targetsToSave = editStockTargets
        .filter(t => t.target_qty !== '' && t.target_qty !== null && t.target_qty !== undefined)
        .map(t => ({ item_id: t.item_id, source_type: t.source_type, source_id: t.source_id, target_qty: parseInt(t.target_qty) || 0 }));
      if (targetsToSave.length > 0) {
        await predictiveRestockAPI.upsertTargets(targetsToSave);
      }
      toast.success('Item updated successfully');
      setEditingId(null);
      setEditStockTargets([]);
      fetchItems({ categoryFilter, silentRetry: true });
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  // Category management functions
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    setSavingCategory(true);
    try {
      const payload = { 
        name: newCategoryName.trim(),
        group_id: newCategoryGroupId && newCategoryGroupId !== 'none' ? newCategoryGroupId : null,
        thumbnail_url: newCategoryThumbnailUrl.trim() || null
      };
      await categoriesAPI.create(payload);
      toast.success('Category created');
      setNewCategoryName('');
      setNewCategoryGroupId('');
      setNewCategoryThumbnailUrl('');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editCategoryName.trim() || !editingCategory) return;
    setSavingCategory(true);
    try {
      const payload = { 
        name: editCategoryName.trim(),
        group_id: editCategoryGroupId && editCategoryGroupId !== 'none' ? editCategoryGroupId : null,
        thumbnail_url: editCategoryThumbnailUrl.trim() || null
      };
      await categoriesAPI.update(editingCategory, payload);
      toast.success('Category updated');
      setEditingCategory(null);
      setEditCategoryName('');
      setEditCategoryGroupId('');
      setEditCategoryThumbnailUrl('');
      fetchCategories();
      fetchItems({ categoryFilter });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Delete category "${categoryName}"? Items in this category will become uncategorized.`)) return;
    try {
      const response = await categoriesAPI.delete(categoryId);
      toast.success(response.data.message || 'Category deleted');
      fetchCategories();
      if (categoryFilter === categoryId) {
        setCategoryFilter('all');
      }
      // Always refresh items in case some were reassigned
      fetchItems({ categoryFilter: categoryFilter === categoryId ? 'all' : categoryFilter });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  // Category Group Handlers
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    setSavingGroup(true);
    try {
      await categoryGroupsAPI.create({ name: newGroupName.trim() });
      toast.success('Category group created');
      setNewGroupName('');
      fetchCategoryGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    } finally {
      setSavingGroup(false);
    }
  };

  const handleUpdateGroup = async () => {
    if (!editGroupName.trim() || !editingGroup) return;
    setSavingGroup(true);
    try {
      await categoryGroupsAPI.update(editingGroup, { name: editGroupName.trim() });
      toast.success('Category group updated');
      setEditingGroup(null);
      setEditGroupName('');
      fetchCategoryGroups();
      fetchCategories(); // Refresh to get updated group names
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update group');
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Delete group "${groupName}"? Categories in this group will become ungrouped.`)) return;
    try {
      const response = await categoryGroupsAPI.delete(groupId);
      toast.success(response.data.message || 'Group deleted');
      fetchCategoryGroups();
      fetchCategories();
      if (groupFilter === groupId) {
        setGroupFilter('all');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete group');
    }
  };

  const startEditCategory = (cat) => {
    setEditingCategory(cat.id);
    setEditCategoryName(cat.name);
    setEditCategoryGroupId(cat.group_id || 'none');
    setEditCategoryThumbnailUrl(cat.thumbnail_url || '');
  };

  // Bulk category delete handler
  const handleBulkDeleteCategories = async () => {
    if (selectedCategoryIds.size === 0) return;
    
    setDeletingCategories(true);
    try {
      const response = await categoriesAPI.bulkDelete(Array.from(selectedCategoryIds));
      toast.success(response.data.message);
      setSelectedCategoryIds(new Set());
      fetchCategories();
      fetchItems({ categoryFilter });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete categories');
    } finally {
      setDeletingCategories(false);
    }
  };

  // Toggle category selection for bulk delete
  const toggleCategorySelection = (categoryId) => {
    setSelectedCategoryIds(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Open clear category confirmation dialog
  const openClearCategoryDialog = (category, e) => {
    e.stopPropagation(); // Prevent tile click
    setCategoryToClear(category);
    setClearCategoryDialogOpen(true);
  };

  // Clear category handler - uses category_id fast path
  const handleClearCategory = async () => {
    if (!categoryToClear) return;
    
    setClearingCategory(true);
    try {
      const response = await itemsAPI.bulkRemoveCategory({
        category_id: categoryToClear.id
      });
      
      toast.success(`${response.data.success_count} item(s) moved to Uncategorized`);
      setClearCategoryDialogOpen(false);
      setCategoryToClear(null);
      
      // Refresh counts without full reload
      fetchCategoriesWithCounts();
      // Also refresh items list if viewing that category
      if (categoryFilter === categoryToClear.id) {
        fetchItems({ categoryFilter: categoryToClear.id });
      }
    } catch (error) {
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers?.['retry-after'] || 60;
        toast.error(`System busy. Please try again in ${retryAfter} seconds.`);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to clear category');
      }
    } finally {
      setClearingCategory(false);
    }
  };

  const startEditGroup = (group) => {
    setEditingGroup(group.id);
    setEditGroupName(group.name);
  };

  const openCommentsDialog = async (item) => {
    setSelectedItem(item);
    setCommentsDialogOpen(true);
    setLoadingComments(true);
    try {
      const res = await itemCommentsAPI.list(item.id);
      setComments(res.data);
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedItem) return;
    
    setSubmittingComment(true);
    try {
      const res = await itemCommentsAPI.add(selectedItem.id, newComment.trim());
      setComments([...comments, res.data]);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Bulk selection functions
  const isItemSelected = (itemId) => {
    if (selectAllMode) {
      return !excludedIds.has(itemId);
    }
    return selectedIds.has(itemId);
  };

  const toggleItemSelection = (itemId) => {
    if (selectAllMode) {
      // In global mode: toggle this item in/out of the exclusion set
      const newExcluded = new Set(excludedIds);
      if (newExcluded.has(itemId)) {
        newExcluded.delete(itemId);  // Re-include
      } else {
        newExcluded.add(itemId);     // Exclude
      }
      // If everything is excluded, exit selectAll mode
      if (newExcluded.size >= matchingCount) {
        setSelectAllMode(false);
        setFrozenQuery(null);
        setExcludedIds(new Set());
        setSelectedIds(new Set());
      } else {
        setExcludedIds(newExcluded);
      }
      return;
    }
    const newSelected = new Set(selectedIds);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    // Select all on current page only
    setSelectAllMode(false);
    setFrozenQuery(null);
    setExcludedIds(new Set());
    setSelectedIds(new Set(items.map(item => item.id)));
  };
  
  const selectAllMatching = () => {
    // Global selection: all matching items across all pages
    setSelectAllMode(true);
    setFrozenQuery({
      categoryId: categoryFilter !== 'all' ? categoryFilter : null,
      search: debouncedSearch || null
    });
    setExcludedIds(new Set());
    setSelectedIds(new Set()); // Not used in selectAll mode
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectAllMode(false);
    setFrozenQuery(null);
    setExcludedIds(new Set());
  };
  
  // Effective selection count
  const effectiveSelectionCount = selectAllMode 
    ? Math.max(0, matchingCount - excludedIds.size) 
    : selectedIds.size;

  const openBulkAction = (actionType) => {
    setBulkActionType(actionType);
    setBulkCategoryId('uncategorized');
    setBulkReorderThreshold('');
    setBulkActionDialogOpen(true);
  };
  
  // Poll job status
  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const response = await itemsAPI.getBulkJobStatus(jobId);
      const job = response.data;
      setJobProgress(job);
      
      if (job.status === 'completed' || job.status === 'failed') {
        setActiveJobId(null);
        if (job.status === 'completed') {
          const result = job.result_data || {};
          const successCount = result.success_count || result.deleted_count || 0;
          const failureCount = result.failure_count || result.blocked_count || 0;
          toast.success(`Operation complete: ${successCount} succeeded, ${failureCount} failed`);
        } else {
          toast.error(job.error_message || 'Job failed');
        }
        // Refresh items list
        fetchItems({ page, pageSize, categoryFilter, search: debouncedSearch });
      }
    } catch (error) {
      console.error('Failed to poll job status', error);
    }
  }, [page, pageSize, categoryFilter, debouncedSearch]);
  
  // Poll active job
  useEffect(() => {
    if (!activeJobId) return;
    
    const interval = setInterval(() => {
      pollJobStatus(activeJobId);
    }, 2000);
    
    return () => clearInterval(interval);
  }, [activeJobId, pollJobStatus]);
  
  // NEW: Bulk archive handler (deactivate)
  const executeBulkArchive = async () => {
    if (effectiveSelectionCount === 0) return;
    
    setBulkActionSaving(true);
    try {
      let response;
      
      if (selectAllMode && frozenQuery) {
        // Query-based archive (always job-based)
        response = await itemsAPI.bulkArchiveByQuery(frozenQuery.categoryId, frozenQuery.search);
        setActiveJobId(response.data.job_id);
        setJobProgressDialogOpen(true);
        toast.info(`Archiving ${effectiveSelectionCount} items in background...`);
      } else if (selectedIds.size > 500) {
        // Too many for inline, use job
        response = await itemsAPI.bulkArchiveJob(Array.from(selectedIds));
        setActiveJobId(response.data.job_id);
        setJobProgressDialogOpen(true);
        toast.info(`Archiving ${selectedIds.size} items in background...`);
      } else {
        // Inline archive
        response = await itemsAPI.bulkArchive(Array.from(selectedIds));
        const result = response.data;
        toast.success(`Archived ${result.success_count} item(s)`);
        
        // Optimistic UI removal if hideInactive is on
        if (hideInactive) {
          setItems(prev => prev.filter(item => !selectedIds.has(item.id)));
        }
        fetchItems({ page, pageSize, categoryFilter, search: debouncedSearch });
      }
      
      clearSelection();
      setBulkActionDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk archive failed');
    } finally {
      setBulkActionSaving(false);
    }
  };
  
  // NEW: Bulk hard delete handler
  const executeBulkHardDelete = async () => {
    if (effectiveSelectionCount === 0) return;
    
    setBulkActionSaving(true);
    try {
      let response;
      
      if (selectAllMode && frozenQuery) {
        // Query-based hard delete (always job-based)
        response = await itemsAPI.bulkHardDeleteByQuery(frozenQuery.categoryId, frozenQuery.search);
      } else {
        // ID-based hard delete (always job-based)
        response = await itemsAPI.bulkHardDelete(Array.from(selectedIds));
      }
      
      setActiveJobId(response.data.job_id);
      setJobProgressDialogOpen(true);
      toast.info(`Hard delete job started for ${effectiveSelectionCount} items. Items with references will be skipped.`);
      
      clearSelection();
      setBulkActionDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk hard delete failed');
    } finally {
      setBulkActionSaving(false);
    }
  };
  
  // Bulk remove category assignment (P1-B)
  const executeBulkRemoveCategory = async () => {
    if (effectiveSelectionCount === 0) return;
    
    setBulkActionSaving(true);
    try {
      let requestData;
      
      if (selectAllMode && frozenQuery) {
        // Query-based: use category_id filter
        requestData = {
          category_id: frozenQuery.categoryId === 'all' ? null : frozenQuery.categoryId,
          search: frozenQuery.search || null
        };
      } else {
        // ID-based: use explicit item IDs
        requestData = {
          item_ids: Array.from(selectedIds)
        };
      }
      
      const response = await itemsAPI.bulkRemoveCategory(requestData);
      
      toast.success(response.data.message);
      clearSelection();
      setBulkActionDialogOpen(false);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove category assignment');
    } finally {
      setBulkActionSaving(false);
    }
  };
  
  // Download failure CSV from job results
  const downloadFailureCsv = () => {
    if (!jobProgress?.result_data?.results) return;
    
    const failures = jobProgress.result_data.results.filter(r => !r.success && !r.deleted);
    if (failures.length === 0) {
      toast.info('No failures to download');
      return;
    }
    
    const headers = ['item_id', 'item_name', 'reason', 'message'];
    const rows = failures.map(f => [
      f.item_id || '',
      f.item_name || '',
      f.reason || (f.blocked ? 'has_references' : 'error'),
      f.message || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk_operation_failures_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success(`Downloaded ${failures.length} failure records`);
  };

  const executeBulkAction = async () => {
    if (selectedIds.size === 0) return;
    
    setBulkActionSaving(true);
    try {
      const itemIds = Array.from(selectedIds);
      
      if (bulkActionType === 'category') {
        const categoryId = bulkCategoryId === 'uncategorized' ? null : bulkCategoryId;
        await itemsAPI.bulkUpdateCategory(itemIds, categoryId);
        toast.success(`Updated category for ${itemIds.length} item(s)`);
      } else if (bulkActionType === 'reorder') {
        if (!bulkReorderThreshold || isNaN(parseInt(bulkReorderThreshold))) {
          toast.error('Please enter a valid reorder threshold');
          setBulkActionSaving(false);
          return;
        }
        await itemsAPI.bulkUpdateReorderThreshold(itemIds, parseInt(bulkReorderThreshold));
        toast.success(`Updated reorder threshold for ${itemIds.length} item(s)`);
      } else if (bulkActionType === 'delete') {
        await itemsAPI.bulkDelete(itemIds);
        toast.success(`Deleted ${itemIds.length} item(s)`);
      }
      
      setBulkActionDialogOpen(false);
      setSelectedIds(new Set());
      fetchItems({ categoryFilter });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk operation failed');
    } finally {
      setBulkActionSaving(false);
    }
  };

  const handleExport = async (mode) => {
    // mode: 'all' | 'filtered' | 'selected'
    try {
      let itemIds = null;
      let filterParams = {};
      let label = 'all';

      if (mode === 'selected') {
        if (!selectAllMode && selectedIds.size === 0) {
          toast.info('No items selected');
          return;
        }
        if (selectAllMode) {
          // Export all matching, minus excluded
          filterParams = {
            search: frozenQuery?.search || undefined,
            categoryId: frozenQuery?.categoryId || undefined,
          };
          if (excludedIds.size > 0) {
            filterParams.excludeIds = Array.from(excludedIds).join(',');
          }
          itemIds = null;
          label = `${effectiveSelectionCount} matching`;
        } else {
          itemIds = Array.from(selectedIds);
          label = `${itemIds.length} selected`;
        }
      } else if (mode === 'filtered') {
        filterParams = {
          search: debouncedSearch || undefined,
          categoryId: categoryFilter !== 'all' ? categoryFilter : undefined,
        };
        label = debouncedSearch || categoryFilter !== 'all' ? 'filtered' : 'all';
      }
      // mode === 'all' → no ids, no filters

      const response = await itemsAPI.exportSelected(itemIds || [], filterParams);
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `parts_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${label} items`);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Permission denied');
      } else {
        toast.error(error.response?.data?.detail || 'Export failed');
      }
    }
  };

  const handleBulkExport = () => handleExport('selected');
  
  // Archive/Restore handlers
  const [stockWarning, setStockWarning] = useState(null); // {item_name, total_qty_on_hand, warehouses, vehicles}
  const handleArchive = async () => {
    if (!archiveTarget) return;
    setArchiveLoading(true);
    try {
      await itemsAPI.archive(archiveTarget.item.id);
      toast.success(`${archiveTarget.item.name} has been archived`);
      setArchiveTarget(null);
      setStockWarning(null);
      fetchItems({ categoryFilter });
      fetchArchivedWithStock();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Stock guard: backend returned 409 with structured detail → surface a confirmation
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'ITEM_HAS_STOCK') {
        setStockWarning(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to archive item');
      }
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleArchiveConfirmForce = async () => {
    if (!archiveTarget) return;
    setArchiveLoading(true);
    try {
      await itemsAPI.archive(archiveTarget.item.id, { force: true });
      toast.success(`${archiveTarget.item.name} archived — stock rows preserved and can be restored anytime.`);
      setArchiveTarget(null);
      setStockWarning(null);
      fetchItems({ categoryFilter });
      fetchArchivedWithStock();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to archive item');
    } finally {
      setArchiveLoading(false);
    }
  };
  
  const handleRestore = async () => {
    if (!archiveTarget) return;
    setArchiveLoading(true);
    try {
      await itemsAPI.restore(archiveTarget.item.id);
      toast.success(`${archiveTarget.item.name} has been restored`);
      setArchiveTarget(null);
      fetchItems({ categoryFilter });
      fetchArchivedWithStock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore item');
    } finally {
      setArchiveLoading(false);
    }
  };

  // Hard delete handler
  const handleHardDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeleteBlockers(null);
    try {
      const res = await itemsAPI.hardDelete(deleteTarget.id);
      const data = res.data;
      if (data.blocked) {
        setDeleteBlockers(data.blockers);
        return;
      }
      toast.success(`"${deleteTarget.name}" permanently deleted`);
      setDeleteTarget(null);
      setItems(prev => prev.filter(i => i.id !== deleteTarget.id));
      setTotal(prev => prev - 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Layout>
      <DuplicatePartWarning {...dupCheck.dialogProps} />
      <div className="space-y-6">
        {/* Archived-with-stock warning — surfaces parts that were archived but still hold inventory */}
        {archivedWithStock.total_items > 0 && (
          <div
            className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 px-4 py-3"
            data-testid="archived-with-stock-alert"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                {archivedWithStock.total_items} archived {archivedWithStock.total_items === 1 ? 'part' : 'parts'} still {archivedWithStock.total_items === 1 ? 'holds' : 'hold'} stock ({archivedWithStock.total_qty_on_hand} units total)
              </p>
              <p className="text-xs text-amber-800/80 dark:text-amber-300/90 mt-0.5">
                These parts were archived but still have inventory in warehouses or vehicles. Stock isn't lost — it's just hidden until restored.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-amber-400 hover:bg-amber-100 dark:border-amber-600 dark:hover:bg-amber-900/40"
                onClick={() => setArchivedAlertOpen(true)}
                data-testid="archived-with-stock-review"
              >
                Review
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-amber-800 dark:text-amber-300"
                onClick={() => setShowArchived(true)}
                data-testid="archived-with-stock-go-to-archived"
              >
                Open Archived view
              </Button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground">{t('catalogue.title')}</h1>
            <p className="text-muted-foreground mt-1 text-sm md:text-base">{t('catalogue.subtitle')}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('catalogue.tagline')}</p>
          </div>
          {/* Desktop Header Buttons (only on Active tab) */}
        {!showArchived && (
          <div className="hidden md:flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setCategoryDialogOpen(true)} data-testid="manage-categories-btn">
              <Tag className="w-4 h-4 mr-2" />
              {t('catalogue.categories')}
            </Button>
            {isOwner && (
              <Button
                variant="outline"
                onClick={() => navigate('/catalogue/duplicates')}
                data-testid="find-duplicates-btn"
                title="Owner-only: review and merge duplicate parts"
              >
                <Layers className="w-4 h-4 mr-2" />
                {t('catalogue.findDuplicates')}
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/catalogue/import')} data-testid="import-btn">
              <Upload className="w-4 h-4 mr-2" />
              {t('common.import')}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-btn">
                  <Download className="w-4 h-4 mr-2" />
                  {t('common.export')}
                  <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('all')} data-testid="export-all">
                  {t('catalogue.exportAll')}
                </DropdownMenuItem>
                {(debouncedSearch || categoryFilter !== 'all') && (
                  <DropdownMenuItem onClick={() => handleExport('filtered')} data-testid="export-filtered">
                    {t('catalogue.exportFiltered')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => handleExport('selected')}
                  disabled={selectedIds.size === 0 && !selectAllMode}
                  data-testid="export-selected"
                >
                  Export Selected{effectiveSelectionCount > 0 ? ` (${effectiveSelectionCount})` : ''}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button className="h-12 px-4 font-bold btn-press" data-testid="add-item-btn" onClick={() => setDialogOpen(true)}>
                  <Plus className="w-5 h-5 mr-2" />
                  Add Part
                </Button>
            <AppDrawer
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              title="Add Part to Catalog"
              description="Create a part here once, then add it to vehicles or warehouses to track quantities."
              size="lg"
              isDirty={!!(formData.name || formData.sku || formData.unit_cost || formData.billable_price)}
              data-testid="add-part-drawer"
              footer={
                <>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" form="add-part-form" disabled={saving} data-testid="save-item-btn">
                    {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : 'Add Item'}
                  </Button>
                </>
              }
            >
                <form id="add-part-form" onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-bold uppercase tracking-wide">
                      Item Name *
                    </Label>
                    <Input
                      id="name"
                      placeholder="e.g., PVC Pipe 1/2in"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="h-12 text-lg"
                      data-testid="item-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-bold uppercase tracking-wide">
                      Categories
                    </Label>
                    <MultiSelectCombobox
                      options={categories.map(cat => ({ value: cat.id, label: cat.name }))}
                      values={formData.category_ids}
                      onValuesChange={(newValues) => setFormData({ ...formData, category_ids: newValues })}
                      placeholder="Search and select categories..."
                      emptyText="No categories found"
                      className="w-full"
                      data-testid="category-multi-select"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sku" className="text-sm font-bold uppercase tracking-wide">
                      SKU
                    </Label>
                    <Input
                      id="sku"
                      placeholder="e.g., PVC-050"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      className="h-12 text-lg font-mono"
                      data-testid="item-sku-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-bold uppercase tracking-wide">
                      Unit of Measure
                    </Label>
                    <Select 
                      value={formData.uom} 
                      onValueChange={(value) => setFormData({ ...formData, uom: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="item-uom-select">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {UOM_OPTIONS.map((uom) => (
                          <SelectItem key={uom.code} value={uom.code}>
                            {uom.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">How this item is measured for quantity and pricing.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="manufacturer_part_number" className="text-sm font-bold uppercase tracking-wide">
                      Manufacturer Part # <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="manufacturer_part_number"
                      placeholder="e.g., MFG-12345"
                      value={formData.manufacturer_part_number}
                      onChange={(e) => setFormData({ ...formData, manufacturer_part_number: e.target.value })}
                      className="h-12 text-lg font-mono"
                      data-testid="item-mfg-part-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit_cost" className="text-sm font-bold uppercase tracking-wide">
                      Unit Cost ($)
                    </Label>
                    <Input
                      id="unit_cost"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.unit_cost}
                      onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                      className="h-12 text-lg font-mono"
                      data-testid="item-cost-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="billable_price" className="text-sm font-bold uppercase tracking-wide">
                      Billable Price ($)
                    </Label>
                    <Input
                      id="billable_price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g., 49.99"
                      value={formData.billable_price}
                      onChange={(e) => setFormData({ ...formData, billable_price: e.target.value })}
                      className="h-12 text-lg font-mono"
                      data-testid="item-billable-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reorder_threshold" className="text-sm font-bold uppercase tracking-wide">
                      Reorder Threshold
                    </Label>
                    <IntegerQuantityInput
                      id="reorder_threshold"
                      min="0"
                      placeholder="e.g., 5"
                      value={formData.reorder_threshold}
                      onChange={(val) => setFormData({ ...formData, reorder_threshold: val })}
                      className="h-12 text-lg font-mono"
                      data-testid="item-threshold-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="external_url" className="text-sm font-bold uppercase tracking-wide">
                      Supply House Link
                    </Label>
                    <Input
                      id="external_url"
                      type="url"
                      placeholder="https://supplier.com/item/123"
                      value={formData.external_url}
                      onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
                      className="h-12"
                      data-testid="item-external-url-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-bold uppercase tracking-wide">Part Image</Label>
                    <div className="flex items-center gap-3">
                      {createImageFile ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Image className="w-4 h-4 text-primary" />
                          <span className="truncate max-w-[180px]">{createImageFile.name}</span>
                          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => setCreateImageFile(null)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors" data-testid="create-item-image-input">
                          <Camera className="w-4 h-4" />
                          Upload image
                          <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => handleImageSelect(e.target.files[0], setCreateImageFile)} />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">JPG, PNG, or WebP. Max 2 MB.</p>
                  </div>
                </form>
            </AppDrawer>
          </div>
        )}
          
          {/* Mobile Header - Categories + overflow menu (only on Active tab) */}
          {!showArchived && (
            <div className="md:hidden flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCategoryDialogOpen(true)}
              >
                <Tag className="w-4 h-4 mr-1.5" />
                Categories
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="mobile-overflow-menu">
                    <Download className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/catalogue/import')}>
                    <Upload className="w-4 h-4 mr-2" />Import
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('all')}>
                    <Download className="w-4 h-4 mr-2" />Export All
                  </DropdownMenuItem>
                  {effectiveSelectionCount > 0 && (
                    <DropdownMenuItem onClick={() => handleExport('selected')}>
                      <Download className="w-4 h-4 mr-2" />Export Selected ({effectiveSelectionCount})
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
        
        {/* Active/Archived Tabs with View Toggle */}
        <div className="flex items-center justify-between border-b border-border">
          <div className="flex gap-2">
            <button
              onClick={() => setShowArchived(false)}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                !showArchived 
                  ? 'text-primary' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-active-items"
            >
              Active
              {!showArchived && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
            <button
              onClick={() => setShowArchived(true)}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                showArchived 
                  ? 'text-primary' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-archived-items"
            >
              Archived
              {showArchived && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
          </div>
          
          {/* View Toggle (only on Active tab) */}
          {!showArchived && (
            <div className="hidden md:flex items-center gap-1 pr-2">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-primary/10 text-primary' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title="List View"
                data-testid="view-mode-list"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('tiles')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'tiles' 
                    ? 'bg-primary/10 text-primary' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title="Categories View"
                data-testid="view-mode-tiles"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Desktop Filters (only on Active tab) */}
        {!showArchived && (
          <div className="hidden md:flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Phase 3.5: Inline Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search parts..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9 h-9 w-[200px] lg:w-[280px]"
                  data-testid="parts-search-input"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => setSearchInput('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                  >
                    <X className="w-3 h-3 text-muted-foreground" />
                  </button>
                )}
                {searchInput !== debouncedSearch && (
                  <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>
              
              {/* Filter Dropdown Button */}
              <Popover open={filterDropdownOpen} onOpenChange={setFilterDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="h-9 px-3 gap-2"
                    data-testid="filter-dropdown-btn"
                  >
                    <Filter className="w-4 h-4" />
                    Filter
                    {(categoryFilter !== 'all' || groupFilter !== 'all') && (
                      <span className="w-2 h-2 rounded-full bg-primary" />
                    )}
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-72 p-4">
                  <div className="space-y-4">
                    <p className="text-sm font-medium text-muted-foreground">Filter by:</p>
                    
                    {/* Main Category Filter */}
                    {categoryGroups.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-sm">Main Category</Label>
                        <SearchableSelect
                          options={[
                            { value: 'all', label: 'All Main Categories' },
                            { value: 'ungrouped', label: 'Uncategorized' },
                            ...categoryGroups.map(g => ({ value: g.id, label: g.name }))
                          ]}
                          value={groupFilter}
                          onValueChange={setGroupFilter}
                          placeholder="All Main Categories"
                          emptyText="No main categories found."
                        />
                      </div>
                    )}
                    
                    {/* Sub Category Filter */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Sub Category</Label>
                        {categoryFilter !== 'all' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                            onClick={clearCategoryFilter}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      <SearchableSelect
                        options={[
                          { value: 'all', label: 'All Categories' },
                          { value: 'uncategorized', label: 'Uncategorized' },
                          ...filteredCategories.map(cat => ({ 
                            value: cat.id, 
                            label: cat.group_name ? `${cat.name} (${cat.group_name})` : cat.name
                          }))
                        ]}
                        value={categoryFilter}
                        onValueChange={setCategoryFilter}
                        placeholder="All Categories"
                        emptyText="No categories found."
                        clearable={categoryFilter !== 'all'}
                        clearValue="all"
                      />
                    </div>
                    
                    {/* Clear Filters */}
                    {(categoryFilter !== 'all' || groupFilter !== 'all') && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full"
                        onClick={() => { clearCategoryFilter(); setGroupFilter('all'); }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear Filters
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              
              {/* Active filter indicator - shown outside dropdown */}
              {(categoryFilter !== 'all' || groupFilter !== 'all') && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <span>
                    {groupFilter !== 'all' && categoryGroups.find(g => g.id === groupFilter)?.name}
                    {groupFilter !== 'all' && categoryFilter !== 'all' && ' / '}
                    {categoryFilter !== 'all' && (
                      categoryFilter === 'uncategorized' 
                        ? 'Uncategorized' 
                        : categories.find(c => c.id === categoryFilter)?.name
                    )}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-2"
                    onClick={() => { clearCategoryFilter(); setGroupFilter('all'); }}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          
            {/* Print Labels + Selection controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-9"
                onClick={() => setBulkLabelPrinterOpen(true)}
                disabled={items.length === 0}
                data-testid="print-labels-btn"
              >
                <Printer className="w-4 h-4 mr-1.5" />
                Print Labels
              </Button>
            {isOwner && items.length > 0 && (
              <div className="flex items-center gap-2">
                {/* Hide Inactive Toggle */}
                <div className="flex items-center gap-2 mr-4">
                  <Switch
                    id="hide-inactive"
                    checked={hideInactive}
                    onCheckedChange={setHideInactive}
                    data-testid="hide-inactive-toggle"
                  />
                  <Label htmlFor="hide-inactive" className="text-sm text-muted-foreground cursor-pointer">
                    Hide inactive
                  </Label>
                </div>
                
                <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-page-btn">
                  Select Page
                </Button>
                {matchingCount > items.length && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={selectAllMatching}
                    className={selectAllMode ? 'bg-primary/10 border-primary' : ''}
                    data-testid="select-all-matching-btn"
                  >
                    Select All {matchingCount.toLocaleString()}
                  </Button>
                )}
                {(selectedIds.size > 0 || selectAllMode) && (
                  <Button variant="outline" size="sm" onClick={clearSelection} data-testid="clear-selection-btn">
                    Clear ({effectiveSelectionCount.toLocaleString()})
                  </Button>
                )}
              </div>
            )}
            </div>
          </div>
        )}
        
        {/* Select All Matching Banner */}
        {selectAllMode && frozenQuery && (
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 flex items-center justify-between" data-testid="select-all-banner">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">
                All {matchingCount.toLocaleString()} matching items selected
                {frozenQuery.categoryId && ' in filtered category'}
                {frozenQuery.search && ` matching "${frozenQuery.search}"`}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
        
        {/* Bulk Action Toolbar */}
        {/* Unified Bulk Action Bar */}
        {isOwner && effectiveSelectionCount > 0 && !showArchived && (
          <div className="sticky top-16 z-10 bg-primary text-primary-foreground rounded-lg p-3 flex items-center justify-between flex-wrap gap-2 shadow-lg" data-testid="bulk-action-toolbar">
            <span className="text-sm font-semibold">
              {effectiveSelectionCount.toLocaleString()} item{effectiveSelectionCount !== 1 ? 's' : ''} selected
            </span>
            {/* Desktop actions */}
            <div className="hidden sm:flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => openBulkAction('category')} data-testid="bulk-assign-category-btn">
                <Tag className="w-4 h-4 mr-1" />
                Assign Category
              </Button>
              <Button variant="secondary" size="sm" onClick={() => openBulkAction('remove-category')} data-testid="bulk-remove-category-btn">
                <FolderMinus className="w-4 h-4 mr-1" />
                Remove Category
              </Button>
              <Button variant="secondary" size="sm" onClick={() => openBulkAction('reorder')} data-testid="bulk-set-reorder-btn">
                Set Reorder
              </Button>
              <Button variant="secondary" size="sm" onClick={() => openBulkAction('archive')} data-testid="bulk-deactivate-btn">
                <Archive className="w-4 h-4 mr-1" />
                Deactivate
              </Button>
              {selectedIds.size > 0 && !selectAllMode && (
                <Button variant="secondary" size="sm" onClick={handleBulkExport} data-testid="bulk-export-btn">
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={() => setBulkLabelPrinterOpen(true)} data-testid="bulk-print-labels-btn">
                <Printer className="w-4 h-4 mr-1" />
                Print Labels
              </Button>
              <Button variant="destructive" size="sm" onClick={() => openBulkAction('hard-delete')} data-testid="bulk-hard-delete-btn">
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
            {/* Mobile actions — primary + overflow */}
            <div className="flex sm:hidden items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => openBulkAction('category')}>
                <Tag className="w-4 h-4 mr-1" />
                Assign
              </Button>
              <Button variant="destructive" size="sm" onClick={() => openBulkAction('hard-delete')}>
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
              <KebabMenu
                items={[
                  { label: 'Remove Category', icon: FolderMinus, onClick: () => openBulkAction('remove-category') },
                  { label: 'Set Reorder', onClick: () => openBulkAction('reorder') },
                  { label: 'Deactivate', icon: Archive, onClick: () => openBulkAction('archive') },
                  { label: 'Print Labels', icon: Printer, onClick: () => setBulkLabelPrinterOpen(true) },
                  ...(selectedIds.size > 0 && !selectAllMode ? [{ label: 'Export', icon: Download, onClick: handleBulkExport }] : []),
                ]}
                triggerClassName="text-primary-foreground hover:bg-primary-foreground/20"
              />
            </div>
          </div>
        )}

        {/* Mobile Filters - Search bar + Filters button (only on Active tab) */}
        {items.length > 0 && !showArchived && (
          <div className="md:hidden space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search parts..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="h-10 pl-9 text-sm"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0 relative"
                onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                aria-label="Filters"
              >
                <Filter className="w-4 h-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
              {isOwner && (
                <KebabMenu
                  items={[{ label: 'Export', icon: Download, onClick: handleBulkExport, 'data-testid': 'catalogue-export-kebab' }]}
                  data-testid="catalogue-kebab-menu"
                />
              )}
            </div>

            {/* Mobile Filters Dropdown */}
            {mobileFiltersOpen && (
              <>
                <div 
                  className="fixed inset-0 bg-black/20 z-40"
                  onClick={() => setMobileFiltersOpen(false)}
                />
                <div className="relative z-50 bg-background border rounded-lg shadow-lg p-4 space-y-4">
                  {/* Phase 3.5: Mobile Search Input */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Search</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search parts..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="pl-9 h-10"
                        data-testid="mobile-parts-search-input"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                      />
                      {searchInput && (
                        <button
                          type="button"
                          onClick={() => setSearchInput('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                        >
                          <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Main Category Filter */}
                  {categoryGroups.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Main Category</Label>
                      <SearchableSelect
                        options={[
                          { value: 'all', label: 'All Main Categories' },
                          { value: 'ungrouped', label: 'Uncategorized' },
                          ...categoryGroups.map(g => ({ value: g.id, label: g.name }))
                        ]}
                        value={groupFilter}
                        onValueChange={setGroupFilter}
                        placeholder="All Main Categories"
                        emptyText="No main categories found."
                        className="h-10"
                      />
                    </div>
                  )}
                  
                  {/* Sub Category Filter */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Sub Category</Label>
                      {categoryFilter !== 'all' && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                          onClick={clearCategoryFilter}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <SearchableSelect
                      options={[
                        { value: 'all', label: 'All Categories' },
                        { value: 'uncategorized', label: 'Uncategorized' },
                        ...filteredCategories.map(cat => ({ 
                          value: cat.id, 
                          label: cat.group_name ? `${cat.name} (${cat.group_name})` : cat.name
                        }))
                      ]}
                      value={categoryFilter}
                      onValueChange={setCategoryFilter}
                      placeholder="All Categories"
                      emptyText="No categories found."
                      className="h-10"
                      clearable={categoryFilter !== 'all'}
                      clearValue="all"
                    />
                  </div>

                  {/* Selection controls for owners */}
                  {isOwner && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Selection</Label>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1" onClick={selectAll}>
                          Select All
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1" onClick={clearSelection}>
                          Clear ({selectedIds.size})
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      variant="outline"
                      className="flex-1 h-10"
                      onClick={() => { setCategoryFilter('all'); setGroupFilter('all'); clearSelection(); setSearchInput(''); }}
                    >
                      Clear All
                    </Button>
                    <Button
                      className="flex-1 h-10"
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      Apply
                    </Button>
                  </div>

                  {/* Results count (Phase 3.5: Updated for server-side pagination) */}
                  <div className="text-xs text-muted-foreground text-center">
                    Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total.toLocaleString()} items
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* CATEGORY TILES VIEW — Nested drill-down browsing */}
        {!showArchived && viewMode === 'tiles' && (
          <div className="space-y-4" data-testid="category-browse-view">
            {/* Breadcrumbs */}
            {browseBreadcrumbs.length > 0 && (
              <nav className="flex items-center gap-1 text-sm flex-wrap" data-testid="browse-breadcrumbs">
                <button onClick={() => handleBreadcrumbNav(null)} className="text-primary hover:underline font-medium" data-testid="breadcrumb-root">All Parts</button>
                {browseBreadcrumbs.map((bc, i) => (
                  <React.Fragment key={bc.id}>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    {i === browseBreadcrumbs.length - 1 ? (
                      <span className="font-medium text-foreground">{bc.name}</span>
                    ) : (
                      <button onClick={() => handleBreadcrumbNav(bc.id)} className="text-primary hover:underline font-medium" data-testid={`breadcrumb-${bc.id}`}>{bc.name}</button>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            )}

            {loadingBrowse ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : (
              <>
                {browseCategories.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {!browseParentId && (
                      <button onClick={() => { setCategoryFilter('all'); setViewMode('list'); }}
                        className="group relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all hover:border-primary hover:shadow-md border-border bg-card"
                        data-testid="category-tile-all">
                        <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                          <Package className="w-8 h-8 text-primary" />
                        </div>
                        <span className="font-medium text-sm text-center">All Parts</span>
                        <span className="text-xs text-muted-foreground mt-1">{browseTotalItems.toLocaleString()} items</span>
                      </button>
                    )}
                    {browseCategories.map((cat) => (
                      <button key={cat.id}
                        className="group relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all hover:border-primary hover:shadow-md border-border bg-card cursor-pointer"
                        data-testid={`category-tile-${cat.id}`}
                        onClick={() => cat.child_count > 0 ? handleDrillDown(cat.id) : (() => { setCategoryFilter(cat.id); setViewMode('list'); })()}>
                        <div className="w-16 h-16 rounded-lg overflow-hidden mb-3 group-hover:scale-105 transition-transform">
                          {cat.thumbnail_url ? (<img src={cat.thumbnail_url} alt={cat.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />) : null}
                          <div className={`w-full h-full bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center ${cat.thumbnail_url ? 'hidden' : ''}`}>
                            {cat.child_count > 0 ? <FolderOpen className="w-8 h-8 text-muted-foreground" /> : <Tag className="w-8 h-8 text-muted-foreground" />}
                          </div>
                        </div>
                        <span className="font-medium text-sm text-center line-clamp-2">{cat.name}</span>
                        <span className="text-xs text-muted-foreground mt-1">
                          {cat.child_count > 0 ? <>{cat.child_count} sub categor{cat.child_count === 1 ? 'y' : 'ies'}</> : <>{cat.item_count} item{cat.item_count !== 1 ? 's' : ''}</>}
                        </span>
                        {cat.child_count > 0 && cat.item_count > 0 && <span className="text-xs text-muted-foreground/60">{cat.item_count} direct item{cat.item_count !== 1 ? 's' : ''}</span>}
                      </button>
                    ))}
                  </div>
                )}

                {browseParentId && browseDirectItemCount > 0 && browseCategories.length > 0 && (
                  <div className="pt-2 border-t">
                    <Button variant="outline" className="w-full" onClick={() => { setCategoryFilter(browseParentId); setViewMode('list'); }} data-testid="view-direct-items">
                      <Package className="w-4 h-4 mr-2" /> View {browseDirectItemCount} item{browseDirectItemCount !== 1 ? 's' : ''} directly in this category
                    </Button>
                  </div>
                )}

                {browseParentId && browseCategories.length === 0 && browseDirectItemCount > 0 && (
                  <div className="pt-2">
                    <Button variant="outline" className="w-full" onClick={() => { setCategoryFilter(browseParentId); setViewMode('list'); }} data-testid="view-leaf-items">
                      <Package className="w-4 h-4 mr-2" /> View {browseDirectItemCount} item{browseDirectItemCount !== 1 ? 's' : ''} in this category
                    </Button>
                  </div>
                )}

                {browseCategories.length === 0 && browseDirectItemCount === 0 && (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <Tag className="w-12 h-12 text-muted-foreground mb-4" />
                      {!browseParentId ? (
                        <>
                          <p className="text-lg font-semibold text-foreground">No categories yet</p>
                          <p className="text-muted-foreground mt-1 text-center">Create main categories and sub categories to organize your parts</p>
                          <Button className="mt-4" variant="outline" onClick={() => setCategoryDialogOpen(true)} data-testid="empty-manage-categories-btn">
                            <Tag className="w-4 h-4 mr-2" /> Manage Categories
                          </Button>
                        </>
                      ) : (
                        <>
                          <p className="text-lg font-semibold text-foreground">Empty category</p>
                          <p className="text-muted-foreground mt-1 text-center">No sub categories or items in this category yet</p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        )}


        {/* LIST VIEW - min-height prevents layout shift during search */}
        {(showArchived || viewMode === 'list') && (
          <div className="min-h-[400px]">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              {showArchived ? (
                <>
                  <Archive className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-semibold text-foreground">No archived items</p>
                  <p className="text-muted-foreground mt-1">Archived items will appear here</p>
                </>
              ) : (
                <>
                  <Package className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-semibold text-foreground">
                    {categoryFilter !== 'all' ? 'No parts in this category' : 'Your Parts List is your master catalog'}
                  </p>
                  <p className="text-muted-foreground mt-1 text-center max-w-md">
                    {categoryFilter !== 'all' 
                      ? 'Try a different filter or add parts to this category' 
                      : 'Create parts here once, then assign them to vehicles or warehouses to track quantities and restock levels.'}
                  </p>
                  {categoryFilter === 'all' && (
                    <Button 
                      className="mt-4" 
                      onClick={() => setDialogOpen(true)}
                      data-testid="empty-state-add-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Part
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ) : filteredItems.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Search className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-lg font-semibold text-foreground">No items match your search</p>
              <Button variant="link" onClick={() => setSearchInput('')}>
                Clear search
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white border rounded-lg divide-y divide-border">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className={`px-4 py-3 transition-colors ${
                  item.archived_at ? 'opacity-70' : (!item.is_active ? 'opacity-60' : 'hover:bg-muted/40')
                } ${isItemSelected(item.id) ? 'bg-primary/5' : ''}`}
                data-testid={`item-card-${item.id}`}
              >
                {editingId === item.id ? (
                  /* Edit mode (unchanged — inline form) */
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs font-bold uppercase tracking-wide">Name</Label>
                        <Input
                          value={editData.name}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          className="h-10"
                          data-testid="edit-item-name"
                        />
                      </div>
                      <div className="space-y-1 col-span-2">
                        <Label className="text-xs font-bold uppercase tracking-wide">Categories</Label>
                        <MultiSelectCombobox
                          options={categories.map(cat => ({ value: cat.id, label: cat.name }))}
                          values={editData.category_ids || []}
                          onValuesChange={(newValues) => setEditData({ ...editData, category_ids: newValues })}
                          placeholder="Search and select categories..."
                          emptyText="No categories found"
                          className="w-full"
                          data-testid="edit-category-multi-select"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-bold uppercase tracking-wide">SKU</Label>
                        <Input
                          value={editData.sku}
                          onChange={(e) => setEditData({ ...editData, sku: e.target.value })}
                          className="h-10 font-mono"
                          data-testid="edit-item-sku"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-bold uppercase tracking-wide">Mfg Part #</Label>
                        <Input
                          value={editData.manufacturer_part_number}
                          onChange={(e) => setEditData({ ...editData, manufacturer_part_number: e.target.value })}
                          className="h-10 font-mono"
                          placeholder="e.g., MFG-12345"
                          data-testid="edit-item-mfg-part"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-bold uppercase tracking-wide">Unit Cost</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editData.unit_cost}
                          onChange={(e) => setEditData({ ...editData, unit_cost: e.target.value })}
                          className="h-10 font-mono"
                          data-testid="edit-item-cost"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-bold uppercase tracking-wide">Billable Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editData.billable_price}
                          onChange={(e) => setEditData({ ...editData, billable_price: e.target.value })}
                          className="h-10 font-mono"
                          data-testid="edit-item-billable"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-bold uppercase tracking-wide">Reorder At</Label>
                        <IntegerQuantityInput
                          min="0"
                          value={editData.reorder_threshold}
                          onChange={(val) => setEditData({ ...editData, reorder_threshold: val })}
                          className="h-10 font-mono"
                          data-testid="edit-item-threshold"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-bold uppercase tracking-wide">Supply House Link</Label>
                        <Input
                          type="url"
                          placeholder="https://..."
                          value={editData.external_url}
                          onChange={(e) => setEditData({ ...editData, external_url: e.target.value })}
                          className="h-10"
                          data-testid="edit-item-external-url"
                        />
                      </div>
                    </div>
                    {/* Stock Targets Section */}
                    {editStockTargets.length > 0 && (
                      <div className="border rounded-lg p-3 bg-muted/30" data-testid="stock-targets-section">
                        <div className="flex items-center gap-2 mb-2">
                          <Target className="w-4 h-4 text-indigo-500" />
                          <span className="text-xs font-bold uppercase tracking-wide">Stock Targets</span>
                          <span className="text-xs text-muted-foreground">(for Predictive Restock)</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                          {editStockTargets.map((target, idx) => (
                            <div key={`${target.source_type}-${target.source_id}`} className="flex flex-col gap-1">
                              <div className="flex items-center gap-1">
                                {target.source_type === 'warehouse' ? (
                                  <WarehouseIcon className="w-3 h-3 text-muted-foreground" />
                                ) : (
                                  <Truck className="w-3 h-3 text-muted-foreground" />
                                )}
                                <TruncatedTextWithTooltip
                                  text={target.source_name}
                                  className="text-xs text-muted-foreground"
                                />
                              </div>
                              <Input
                                type="number"
                                min="0"
                                placeholder="0"
                                value={target.target_qty}
                                onChange={(e) => {
                                  const updated = [...editStockTargets];
                                  updated[idx] = { ...updated[idx], target_qty: e.target.value };
                                  setEditStockTargets(updated);
                                }}
                                className="h-8 text-xs font-mono"
                                data-testid={`stock-target-${target.source_type}-${target.source_id}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editData.is_active}
                            onCheckedChange={(checked) => setEditData({ ...editData, is_active: checked })}
                            data-testid="edit-item-active"
                          />
                          <Label className="text-sm">Active</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          {editData.image_thumb_url ? (
                            <>
                              <img src={editData.image_thumb_url} alt="" className="w-8 h-8 rounded object-cover" />
                              <Button type="button" variant="ghost" size="sm" className="text-destructive h-7 px-2 text-xs" onClick={() => handleEditImageDelete(item.id)} data-testid="edit-remove-image-btn">
                                <Trash2 className="w-3 h-3 mr-1" /> Remove
                              </Button>
                            </>
                          ) : (
                            <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors" data-testid="edit-upload-image-btn">
                              <Camera className="w-3.5 h-3.5" /> Add Image
                              <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => e.target.files[0] && handleEditImageUpload(item.id, e.target.files[0])} />
                            </label>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {isOwner && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              // Load the full ItemResponse so the manual-merge
                              // modal has every field it needs for warnings.
                              try {
                                const resp = await itemsAPI.get(item.id);
                                setManualMergeItem(resp.data);
                                setManualMergeOpen(true);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || 'Could not open item');
                              }
                            }}
                            data-testid={`open-manual-merge-${item.id}`}
                            title="Merge this item with another catalogue item"
                          >
                            <GitMerge className="w-4 h-4 mr-1" /> Merge with another item
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={cancelEdit} data-testid="cancel-item-edit">
                          <X className="w-4 h-4 mr-1" /> Cancel
                        </Button>
                        <Button size="sm" onClick={() => saveEdit(item.id)} disabled={editSaving} data-testid="save-item-edit">
                          {editSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Desktop row */}
                    <div className="hidden md:flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {isOwner && (
                          <Checkbox
                            checked={isItemSelected(item.id)}
                            onCheckedChange={() => toggleItemSelection(item.id)}
                            className="w-5 h-5 shrink-0"
                            data-testid={`select-item-${item.id}`}
                          />
                        )}
                        {item.image_thumb_url ? (
                          <ImageThumbnail
                            src={item.image_thumb_url}
                            fullSrc={item.image_url || item.image_thumb_url}
                            alt={item.name}
                            size="md"
                            data-testid={`item-thumb-${item.id}`}
                          />
                        ) : (
                          <ImageThumbnail
                            src={null}
                            alt={item.name}
                            size="md"
                            data-testid={`item-thumb-${item.id}`}
                          />
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-base font-semibold text-foreground">{item.name}</span>
                            {item.category_names && item.category_names.length > 0 ? (
                              item.category_names.map((catName, idx) => (
                                <span key={idx} className="text-xs font-medium px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                                  {catName}
                                </span>
                              ))
                            ) : null}
                            {!item.is_active && (
                              <span className="text-xs font-medium px-2 py-0.5 bg-muted rounded-full text-muted-foreground">
                                Inactive
                              </span>
                            )}
                            {item.external_url && (
                              <a
                                href={item.external_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-medium px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full inline-flex items-center gap-1 hover:bg-blue-200 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                                title={item.external_url}
                              >
                                <ExternalLink className="w-3 h-3" />
                                Supply House
                              </a>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {item.sku && <span className="font-mono">{item.sku}</span>}
                            {item.sku && item.manufacturer_part_number && ' · '}
                            {item.manufacturer_part_number && <span className="font-mono text-xs">Mfg: {item.manufacturer_part_number}</span>}
                            {(item.sku || item.manufacturer_part_number) && item.unit_cost && ' · '}
                            {item.unit_cost && <span>Cost: ${parseFloat(item.unit_cost).toFixed(2)}</span>}
                            {item.unit_cost && item.billable_price && ' · '}
                            {item.billable_price && <span>Billable: ${parseFloat(item.billable_price).toFixed(2)}</span>}
                            {(item.sku || item.manufacturer_part_number || item.unit_cost || item.billable_price) && item.reorder_threshold && ' · '}
                            {item.reorder_threshold && <span>Reorder at {item.reorder_threshold}</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => {
                            setSelectedItemForCodes(item);
                            setCodesManagerOpen(true);
                          }}
                          title="Manage codes"
                        >
                          <QrCode className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => {
                            setSelectedItemForCodes(item);
                            setLabelPrinterOpen(true);
                          }}
                          title="Print labels"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        {!item.archived_at && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => openCommentsDialog(item)} data-testid={`item-comments-${item.id}`}>
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => startEdit(item)} data-testid={`edit-item-${item.id}`}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {isOwner && (
                          item.archived_at ? (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => setArchiveTarget({ item, action: 'restore' })}
                              title="Restore Item"
                              data-testid={`restore-item-${item.id}`}
                            >
                              <ArchiveRestore className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              onClick={() => setArchiveTarget({ item, action: 'archive' })}
                              title="Archive Item"
                              data-testid={`archive-item-${item.id}`}
                            >
                              <Archive className="w-4 h-4" />
                            </Button>
                          )
                        )}
                        {isOwner && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10"
                            onClick={() => { setDeleteBlockers(null); setDeleteTarget(item); }}
                            title="Permanently Delete"
                            data-testid={`delete-item-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Mobile row */}
                    <div className="md:hidden space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {isOwner && (
                            <Checkbox
                              checked={isItemSelected(item.id)}
                              onCheckedChange={() => toggleItemSelection(item.id)}
                              className="w-5 h-5 shrink-0"
                            />
                          )}
                          {item.image_thumb_url ? (
                            <ImageThumbnail
                              src={item.image_thumb_url}
                              fullSrc={item.image_url || item.image_thumb_url}
                              alt={item.name}
                              size="sm"
                            />
                          ) : null}
                          <TruncatedTextWithTooltip
                            as="h3"
                            text={item.name}
                            className="font-semibold text-foreground"
                          />
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                          {item.category_names && item.category_names.length > 0 ? (
                            item.category_names.slice(0, 1).map((catName, idx) => (
                              <span key={idx} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                                {catName}
                              </span>
                            ))
                          ) : item.category_name ? (
                            <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                              {item.category_name}
                            </span>
                          ) : null}
                          {!item.is_active && (
                            <span className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">
                              Inactive
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Metadata line */}
                      <div className="text-xs text-muted-foreground">
                        {item.sku && <span className="font-mono">{item.sku}</span>}
                        {item.sku && item.manufacturer_part_number && ' · '}
                        {item.manufacturer_part_number && <span className="font-mono">Mfg: {item.manufacturer_part_number}</span>}
                        {(item.sku || item.manufacturer_part_number) && item.unit_cost && ' · '}
                        {item.unit_cost && <span>${parseFloat(item.unit_cost).toFixed(2)}</span>}
                        {item.unit_cost && item.billable_price && ' · '}
                        {item.billable_price && <span>Billable ${parseFloat(item.billable_price).toFixed(2)}</span>}
                        {(item.sku || item.manufacturer_part_number || item.unit_cost || item.billable_price) && item.reorder_threshold && ' · '}
                        {item.reorder_threshold && <span>Reorder @ {item.reorder_threshold}</span>}
                      </div>
                      {/* Single-row actions: QR + Print + Supply House inline, overflow ⋯ for rest */}
                      <div className="flex items-center gap-x-3 flex-nowrap">
                        <button
                          onClick={() => {
                            setSelectedItemForCodes(item);
                            setCodesManagerOpen(true);
                          }}
                          className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
                          title="Manage codes"
                        >
                          <QrCode className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedItemForCodes(item);
                            setLabelPrinterOpen(true);
                          }}
                          className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
                          title="Print labels"
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        {item.external_url && (
                          <a
                            href={item.external_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-blue-600 hover:text-blue-800 rounded-md hover:bg-blue-50 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                            title="Supply House"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        {/* Overflow menu for secondary actions */}
                        <KebabMenu
                          className="h-8 w-8"
                          data-testid={`item-overflow-${item.id}`}
                          items={[
                            ...(!item.archived_at ? [
                              { label: 'Edit', icon: Edit2, onClick: () => startEdit(item), 'data-testid': `edit-item-mobile-${item.id}` },
                              { label: 'Notes', icon: MessageSquare, onClick: () => openCommentsDialog(item), 'data-testid': `item-comments-mobile-${item.id}` },
                            ] : []),
                            ...(isOwner ? [
                              item.archived_at
                                ? { label: 'Restore', icon: ArchiveRestore, onClick: () => setArchiveTarget({ item, action: 'restore' }), 'data-testid': `restore-item-mobile-${item.id}` }
                                : { label: 'Archive', icon: Archive, onClick: () => setArchiveTarget({ item, action: 'archive' }), 'data-testid': `archive-item-mobile-${item.id}` },
                              { label: 'Delete', icon: Trash2, onClick: () => { setDeleteBlockers(null); setDeleteTarget(item); }, variant: 'destructive', 'data-testid': `delete-item-mobile-${item.id}` },
                            ] : []),
                          ]}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Phase 3.5: Pagination Controls (only for list view) */}
        {!loading && filteredItems.length > 0 && (showArchived || viewMode === 'list') && (
          <ListPagination
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={loading}
            pageSizeOptions={[15, 25, 50, 100]}
          />
        )}
          </div>
        )}
      </div>
      
      {/* Archive/Restore Confirmation Dialog */}
      <Dialog open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {archiveTarget?.action === 'archive' ? (
                <>
                  <Archive className="w-5 h-5 text-amber-600" />
                  Archive Item
                </>
              ) : (
                <>
                  <ArchiveRestore className="w-5 h-5 text-green-600" />
                  Restore Item
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {archiveTarget?.action === 'archive' 
                ? `Are you sure you want to archive "${archiveTarget?.item?.name}"? It will be hidden from the active parts list but preserved in historical data.`
                : `Are you sure you want to restore "${archiveTarget?.item?.name}"? It will be visible in the active parts list again.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setArchiveTarget(null)} disabled={archiveLoading}>
              Cancel
            </Button>
            <Button 
              variant={archiveTarget?.action === 'archive' ? 'default' : 'default'}
              className={archiveTarget?.action === 'archive' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}
              onClick={archiveTarget?.action === 'archive' ? handleArchive : handleRestore}
              disabled={archiveLoading}
              data-testid={`confirm-${archiveTarget?.action}-item`}
            >
              {archiveLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {archiveTarget?.action === 'archive' ? 'Archive' : 'Restore'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Stock-guard warning: shown when backend returns 409 ITEM_HAS_STOCK */}
      <Dialog open={!!stockWarning} onOpenChange={(open) => !open && setStockWarning(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="archive-stock-warning-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              This part still has stock
            </DialogTitle>
            <DialogDescription>
              <span className="font-medium text-foreground">"{stockWarning?.item_name}"</span> currently holds{' '}
              <span className="font-semibold text-foreground">{stockWarning?.total_qty_on_hand}</span> unit(s) of on-hand stock.
              Archiving will <span className="font-medium">hide it from warehouse and vehicle inventory lists</span>, but the stock rows
              are preserved and the item can be restored at any time from the Archived view.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {stockWarning?.warehouses?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Warehouses</p>
                <ul className="space-y-1">
                  {stockWarning.warehouses.map((w, idx) => (
                    <li key={idx} className="text-sm flex justify-between bg-amber-50 dark:bg-amber-950/30 rounded px-2.5 py-1.5">
                      <span>{w.warehouse_name}</span>
                      <span className="font-semibold">{w.qty_on_hand} units</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {stockWarning?.vehicles?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Vehicles / Vans</p>
                <ul className="space-y-1">
                  {stockWarning.vehicles.map((v, idx) => (
                    <li key={idx} className="text-sm flex justify-between bg-amber-50 dark:bg-amber-950/30 rounded px-2.5 py-1.5">
                      <span>{v.van_name}</span>
                      <span className="font-semibold">{v.qty_on_hand} units</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setStockWarning(null)} disabled={archiveLoading} data-testid="archive-stock-warning-cancel">
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleArchiveConfirmForce}
              disabled={archiveLoading}
              data-testid="archive-stock-warning-confirm"
            >
              {archiveLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Archive anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archived-with-stock REVIEW dialog: lists all archived parts that still hold stock */}
      <Dialog open={archivedAlertOpen} onOpenChange={setArchivedAlertOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="archived-with-stock-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Archived parts that still hold stock
            </DialogTitle>
            <DialogDescription>
              These parts are hidden from warehouse and vehicle inventory views because they were archived,
              but the stock rows are still in the database. Restore a part to make its stock visible again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {archivedWithStock.items.map((it) => (
              <div key={it.item_id} className="rounded-md border border-border p-3 bg-muted/30">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <TruncatedTextWithTooltip
                      as="p"
                      text={it.item_name}
                      className="text-sm font-semibold text-foreground"
                    />
                    {it.sku && <p className="text-xs text-muted-foreground font-mono">{it.sku}</p>}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {it.total_qty_on_hand} unit(s) hidden across{' '}
                      {it.warehouses.length} warehouse(s){it.vehicles.length ? `, ${it.vehicles.length} vehicle(s)` : ''}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => {
                      setArchivedAlertOpen(false);
                      setArchiveTarget({ item: { id: it.item_id, name: it.item_name }, action: 'restore' });
                    }}
                    data-testid={`archived-with-stock-restore-${it.item_id}`}
                  >
                    <ArchiveRestore className="w-3.5 h-3.5 mr-1" />
                    Restore
                  </Button>
                </div>
                {(it.warehouses.length > 0 || it.vehicles.length > 0) && (
                  <ul className="mt-2 space-y-0.5 text-xs">
                    {it.warehouses.map((w, idx) => (
                      <li key={`wh-${idx}`} className="flex justify-between text-muted-foreground">
                        <span><WarehouseIcon className="w-3 h-3 inline mr-1" />{w.warehouse_name}</span>
                        <span className="font-medium text-foreground">{w.qty_on_hand}</span>
                      </li>
                    ))}
                    {it.vehicles.map((v, idx) => (
                      <li key={`van-${idx}`} className="flex justify-between text-muted-foreground">
                        <span><Truck className="w-3 h-3 inline mr-1" />{v.van_name}</span>
                        <span className="font-medium text-foreground">{v.qty_on_hand}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            {archivedWithStock.items.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-6">All clear — no archived parts hold stock.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchivedAlertOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Hard Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteBlockers(null); } }}>
        <AlertDialogContent data-testid="delete-item-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {deleteBlockers ? (
                <><AlertTriangle className="w-5 h-5 text-amber-500" /> Cannot Delete Part</>
              ) : (
                <><Trash2 className="w-5 h-5 text-destructive" /> Permanently Delete Part</>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {deleteBlockers ? (
                  <>
                    <p>
                      <strong>"{deleteTarget?.name}"</strong> cannot be deleted because it is referenced in the following areas:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                      {deleteBlockers.map((b, i) => (
                        <li key={i}>
                          <span className="font-medium">{b.table.replace(/_/g, ' ')}</span> — {b.count} record{b.count !== 1 ? 's' : ''}
                        </li>
                      ))}
                    </ul>
                    <p className="text-sm text-muted-foreground">
                      Use <strong>Archive</strong> instead to hide this part from the active list while preserving historical data.
                    </p>
                  </>
                ) : (
                  <p>
                    This will permanently remove <strong>"{deleteTarget?.name}"</strong>
                    {deleteTarget?.sku ? <> (SKU: {deleteTarget.sku})</> : ''} from your catalogue. All associated data will be lost. This action cannot be undone.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading} data-testid="delete-item-cancel">
              {deleteBlockers ? 'Close' : 'Cancel'}
            </AlertDialogCancel>
            {!deleteBlockers && (
              <AlertDialogAction
                onClick={handleHardDelete}
                disabled={deleteLoading}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="delete-item-confirm"
              >
                {deleteLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</> : 'Delete Permanently'}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Item Comments Dialog */}
      <Dialog open={commentsDialogOpen} onOpenChange={(open) => {
        setCommentsDialogOpen(open);
        if (!open) {
          setSelectedItem(null);
          setComments([]);
          setNewComment('');
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Comments: {selectedItem?.name}
            </DialogTitle>
            <DialogDescription>
              Add notes and context about this item
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0 space-y-3 mt-4">
            {loadingComments ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : comments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No comments yet. Be the first to add context!
              </p>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm">{comment.user_name}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                        </p>
                      </div>
                      <p className="mt-1 text-sm text-foreground">{comment.message}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add Comment Form */}
          <form onSubmit={handleAddComment} className="pt-4 border-t mt-4 shrink-0">
            <div className="space-y-3">
              <Textarea
                placeholder="Add a comment about this item..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={2}
                className="resize-none"
              />
              <div className="flex justify-end">
                <Button type="submit" size="sm" disabled={submittingComment || !newComment.trim()}>
                  {submittingComment ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Add Comment
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Manager Drawer */}
      <CategoryManagerDrawer
        open={categoryDialogOpen}
        onOpenChange={setCategoryDialogOpen}
        onSaved={() => {
          fetchBrowseCategories(browseParentId);
          fetchCategories();
        }}
      />

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {bulkActionType === 'category' && 'Assign Category'}
              {bulkActionType === 'reorder' && 'Set Reorder Threshold'}
              {bulkActionType === 'delete' && 'Delete Items'}
              {bulkActionType === 'archive' && 'Deactivate (Archive) Items'}
              {bulkActionType === 'hard-delete' && 'Delete Items'}
              {bulkActionType === 'remove-category' && 'Remove Category Assignment'}
            </DialogTitle>
            <DialogDescription>
              {bulkActionType === 'category' && `Assign a category to ${effectiveSelectionCount.toLocaleString()} selected item(s).`}
              {bulkActionType === 'reorder' && `Set reorder threshold for ${effectiveSelectionCount.toLocaleString()} selected item(s).`}
              {bulkActionType === 'delete' && `This will delete ${effectiveSelectionCount.toLocaleString()} item(s). Historical activity will be preserved.`}
              {bulkActionType === 'archive' && `This will deactivate ${effectiveSelectionCount.toLocaleString()} item(s). They can be restored later from the Archived tab.`}
              {bulkActionType === 'hard-delete' && `This will delete ${effectiveSelectionCount.toLocaleString()} item(s). Items with existing inventory or history will be skipped.`}
              {bulkActionType === 'remove-category' && `Remove category assignment from ${effectiveSelectionCount.toLocaleString()} item(s). Items will not be deleted.`}
            </DialogDescription>
          </DialogHeader>
          
          {bulkActionType === 'category' && (
            <div className="space-y-4 py-4">
              <Label className="text-sm font-bold uppercase tracking-wide">Category</Label>
              <Select value={bulkCategoryId} onValueChange={setBulkCategoryId}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="uncategorized">Uncategorized</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {bulkActionType === 'reorder' && (
            <div className="space-y-4 py-4">
              <Label className="text-sm font-bold uppercase tracking-wide">Reorder Threshold</Label>
              <IntegerQuantityInput
                min="0"
                placeholder="e.g., 5"
                value={bulkReorderThreshold}
                onChange={(val) => setBulkReorderThreshold(val)}
                className="h-12 text-lg font-mono"
                data-testid="catalogue-bulk-reorder-threshold"
              />
            </div>
          )}
          
          {bulkActionType === 'delete' && (
            <div className="py-4 flex items-center gap-3 p-4 bg-destructive/10 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-destructive shrink-0" />
              <p className="text-sm">
                Are you sure you want to delete {effectiveSelectionCount.toLocaleString()} item(s)? This action cannot be undone.
              </p>
            </div>
          )}
          
          {bulkActionType === 'archive' && (
            <div className="py-4 flex items-center gap-3 p-4 bg-amber-500/10 rounded-lg">
              <Archive className="w-6 h-6 text-amber-600 shrink-0" />
              <p className="text-sm">
                Deactivated items will be moved to the Archived tab and can be restored anytime.
                {selectAllMode && ' This will process in the background.'}
              </p>
            </div>
          )}
          
          {bulkActionType === 'hard-delete' && (
            <div className="py-4 space-y-3">
              <div className="flex items-center gap-3 p-4 bg-destructive/10 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-destructive shrink-0" />
                <div className="text-sm space-y-1">
                  <p className="font-medium">This action is permanent and cannot be undone.</p>
                  <p className="text-muted-foreground">
                    Items referenced in inventory, transfers, or history will be automatically skipped.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {bulkActionType === 'remove-category' && (
            <div className="py-4 flex items-center gap-3 p-4 bg-blue-500/10 rounded-lg">
              <FolderMinus className="w-6 h-6 text-blue-600 shrink-0" />
              <div className="text-sm space-y-1">
                <p className="font-medium">Items will be moved to &ldquo;Uncategorized&rdquo;</p>
                <p className="text-muted-foreground">
                  The items themselves will not be deleted. You can re-assign them to a category later.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant={(bulkActionType === 'delete' || bulkActionType === 'hard-delete') ? 'destructive' : 'default'}
              onClick={() => {
                if (bulkActionType === 'archive') {
                  executeBulkArchive();
                } else if (bulkActionType === 'hard-delete') {
                  executeBulkHardDelete();
                } else if (bulkActionType === 'remove-category') {
                  executeBulkRemoveCategory();
                } else {
                  executeBulkAction();
                }
              }}
              disabled={bulkActionSaving}
              data-testid="bulk-action-confirm-btn"
            >
              {bulkActionSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {bulkActionType === 'delete' && 'Delete Items'}
                  {bulkActionType === 'archive' && 'Deactivate Items'}
                  {bulkActionType === 'hard-delete' && 'Delete Items'}
                  {bulkActionType === 'remove-category' && 'Remove Category'}
                  {(bulkActionType === 'category' || bulkActionType === 'reorder') && 'Apply'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Codes Manager */}
      <ItemCodesManager 
        open={codesManagerOpen}
        onClose={() => {
          setCodesManagerOpen(false);
          setSelectedItemForCodes(null);
        }}
        itemId={selectedItemForCodes?.id}
        itemName={selectedItemForCodes?.name}
        itemSku={selectedItemForCodes?.sku}
      />

      {/* Label Printer */}
      <LabelPrinter 
        open={labelPrinterOpen}
        onClose={() => {
          setLabelPrinterOpen(false);
          setSelectedItemForCodes(null);
        }}
        itemId={selectedItemForCodes?.id}
        itemName={selectedItemForCodes?.name}
        itemSku={selectedItemForCodes?.sku}
      />

      {/* Bulk Label Printer */}
      <BulkLabelPrinter
        open={bulkLabelPrinterOpen}
        onClose={() => setBulkLabelPrinterOpen(false)}
        selectedIds={selectedIds}
        selectAllMode={selectAllMode}
        frozenQuery={frozenQuery}
        currentFilters={{ categoryId: categoryFilter !== 'all' ? categoryFilter : null, search: debouncedSearch }}
        items={items}
        totalFiltered={matchingCount}
      />

      {/* Manual Item Merge (owner-only entry point from the inline edit) */}
      <ManualMergeModal
        open={manualMergeOpen}
        onOpenChange={(o) => {
          setManualMergeOpen(o);
          if (!o) setManualMergeItem(null);
        }}
        currentItem={manualMergeItem}
        onMergeSuccess={async ({ survivingItemId, currentWasKilled }) => {
          // Cancel the inline edit — either the item we were editing is now
          // a tombstone, or we still want a clean state before re-fetching.
          cancelEdit();
          setManualMergeOpen(false);
          setManualMergeItem(null);
          await fetchItems({ categoryFilter });
          if (currentWasKilled && survivingItemId) {
            // Small delay so the just-refreshed list is in state before we
            // try to re-open the surviving item's edit form.
            setTimeout(async () => {
              try {
                const resp = await itemsAPI.get(survivingItemId);
                startEdit(resp.data);
              } catch {
                // If we can't reopen, that's OK — the list refresh already
                // navigated the user visually to the surviving master.
              }
            }, 50);
          }
        }}
      />

      {/* Mobile FAB (only on Active tab) */}
      {!showArchived && (
        <MobileFABContainer data-testid="catalogue-fab-container">
          <MobileFABMenu open={fabOpen} onClose={() => setFabOpen(false)}>
            <MobileFABMenuItem
              icon={Plus}
              onClick={() => { setFabOpen(false); setDialogOpen(true); }}
            >
              Add Item
            </MobileFABMenuItem>
            <MobileFABMenuItem
              icon={Upload}
              onClick={() => { setFabOpen(false); navigate('/catalogue/import'); }}
            >
              Import
            </MobileFABMenuItem>
          </MobileFABMenu>
          <button
            className="w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all active:scale-95"
            onClick={() => setFabOpen(!fabOpen)}
            data-testid="catalogue-mobile-fab"
          >
            <Plus className={`w-5 h-5 transition-transform ${fabOpen ? 'rotate-45' : ''}`} strokeWidth={2.5} />
          </button>
        </MobileFABContainer>
      )}
      
      {/* Job Progress Dialog */}
      <Dialog open={jobProgressDialogOpen} onOpenChange={(open) => {
        if (!open && !activeJobId) {
          setJobProgressDialogOpen(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {activeJobId ? 'Processing...' : 'Operation Complete'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {activeJobId && jobProgress && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{jobProgress.progress_message || 'Processing items...'}</span>
                  <span className="font-mono">
                    {jobProgress.progress_current}/{jobProgress.progress_total}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(jobProgress.progress_current / jobProgress.progress_total) * 100}%` }}
                  />
                </div>
              </>
            )}
            
            {!activeJobId && jobProgress?.result_data && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {(jobProgress.result_data.success_count || jobProgress.result_data.deleted_count || 0).toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">Succeeded</div>
                  </div>
                  <div className="p-3 bg-amber-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-amber-600">
                      {(jobProgress.result_data.failure_count || jobProgress.result_data.blocked_count || 0).toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {jobProgress.result_data.blocked_count !== undefined ? 'Blocked' : 'Failed'}
                    </div>
                  </div>
                </div>
                
                {(jobProgress.result_data.failure_count > 0 || jobProgress.result_data.blocked_count > 0) && (
                  <div className="text-sm text-muted-foreground text-center">
                    <p>Some items could not be processed (e.g., they have existing references).</p>
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="mt-1"
                      onClick={downloadFailureCsv}
                      data-testid="download-failures-btn"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download failure report
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            {activeJobId ? (
              <p className="text-sm text-muted-foreground">Please wait, processing in background...</p>
            ) : (
              <Button onClick={() => setJobProgressDialogOpen(false)}>
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Category Confirmation Dialog */}
      <Dialog open={clearCategoryDialogOpen} onOpenChange={(open) => {
        if (!clearingCategory) {
          setClearCategoryDialogOpen(open);
          if (!open) setCategoryToClear(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FolderMinus className="w-5 h-5" />
              Clear Category
            </DialogTitle>
            <DialogDescription>
              Unassign all items from this category
            </DialogDescription>
          </DialogHeader>
          
          {categoryToClear && (
            <div className="py-4 space-y-4">
              <div className="flex items-center gap-3 p-4 bg-amber-500/10 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
                <div className="text-sm space-y-1">
                  <p className="font-medium">
                    Clear &ldquo;{categoryToClear.name}&rdquo;?
                  </p>
                  <p className="text-muted-foreground">
                    {categoryToClear.active_item_count.toLocaleString()} item{categoryToClear.active_item_count !== 1 ? 's' : ''} will be moved to Uncategorized.
                    Items will not be deleted.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setClearCategoryDialogOpen(false); setCategoryToClear(null); }}
              disabled={clearingCategory}
            >
              Cancel
            </Button>
            <Button 
              variant="default"
              onClick={handleClearCategory}
              disabled={clearingCategory}
              data-testid="clear-category-confirm-btn"
            >
              {clearingCategory ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <FolderMinus className="w-4 h-4 mr-2" />
                  Clear Category
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
